/**
 * A small "what does this column mean" marker, and the sentence behind it.
 *
 * The bubble, the portal, the placement and every listener that closes it are
 * `Tooltip`'s now. Two implementations of a thing that has to be positioned from
 * a rect and torn down on a scroll is one too many, and this was the one that
 * anchored below the trigger and had no caret.
 *
 * What is left is the part that was never about the bubble. The trigger is a
 * real `button` with `type="button"`, so it cannot submit the form it may sit
 * inside, and it carries the column name in its accessible name rather than a
 * bare "info", which would read as forty identical buttons to a screen reader.
 *
 * `aria-expanded` is gone with the rest. It was describing a disclosure, which
 * this is not, and the correct pairing is the `aria-describedby` that `Tooltip`
 * puts on this button while the bubble is open.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { FOCUS_RING } from './control-styles'
import { IconHelpCircle } from './icons'
import { Tooltip, type TooltipPlacement } from './Tooltip'

export interface InfoHintProps {
  /** What this marker explains. Used in the accessible name: "What Daily means". */
  label: string
  /** The explanation. Plain prose, one or two sentences. */
  children: ReactNode
  className?: string
  /** A bold first line above the explanation. */
  heading?: ReactNode
  /** Ask the bubble for a side, such as `left` inside a right-hand panel. */
  placement?: TooltipPlacement
}

/**
 * The `(?)`: a 16px help circle in the faint ink, a shade darker under the
 * pointer, opening its bubble on hover and focus and pinning it on a click.
 */
export function InfoHint({ label, children, className, heading, placement }: InfoHintProps) {
  return (
    // The nudge and the alignment belong on the wrapper, because the wrapper is
    // now the inline box sitting in the line of header text.
    <Tooltip content={children} heading={heading} placement={placement} className="ml-1 align-middle">
      <button
        type="button"
        aria-label={`What ${label} means`}
        className={cn(
          'inline-flex rounded-full text-ink-faint transition-colors',
          'hover:text-ink-subtle focus-visible:text-ink-subtle',
          FOCUS_RING,
          className,
        )}
        onClick={(event) => {
          // Stops a header that sorts on click from sorting because somebody
          // asked what the column means. Pinning still works: the tooltip
          // watches for the click on the way down, before this runs.
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        <IconHelpCircle className="h-4 w-4" />
      </button>
    </Tooltip>
  )
}
